import React, { useState } from "react";
import {
  Button,
  InlineNotification,
  PasswordInput,
  TextInput,
  Toggle,
} from "@carbon/react";
import {
  ArrowLeft,
  Logout,
  Reset,
  Save,
  Security,
  UserAdmin,
} from "@carbon/react/icons";
import { Link } from "react-router-dom";
import {
  loadDashboardConfig,
  resetDashboardConfig,
  saveDashboardConfig,
} from "../config/dashboardConfig";
import AdminDataManager from "./AdminDataManager";
import "./AdminPage.css";

const ADMIN_SESSION_KEY = "sgbv-admin-authenticated";
const TEMPORARY_ADMIN_USERNAME = "mmwanje";
const TEMPORARY_ADMIN_PASSWORD = "Mmwanje123";

const hasAdminSession = () =>
  window.sessionStorage.getItem(ADMIN_SESSION_KEY) === "true";

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = event => {
    event.preventDefault();

    if (
      username === TEMPORARY_ADMIN_USERNAME &&
      password === TEMPORARY_ADMIN_PASSWORD
    ) {
      window.sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
      setError("");
      onLogin();
      return;
    }

    setError("The username or password is incorrect.");
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-card" aria-labelledby="admin-login-title">
        <div className="admin-login-brand">
          <span><Security size={28} /></span>
          <div><strong>SGBV Dashboard</strong><small>Administration</small></div>
        </div>

        <div className="admin-login-heading">
          <span className="admin-login-icon"><UserAdmin size={24} /></span>
          <div>
            <h1 id="admin-login-title">Administrator sign in</h1>
            <p>Enter the temporary administrator credentials to manage dashboard configuration.</p>
          </div>
        </div>

        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Sign-in failed"
            subtitle={error}
          />
        )}

        <form className="admin-login-form" onSubmit={handleSubmit}>
          <TextInput
            id="admin-username"
            labelText="Username"
            autoComplete="username"
            value={username}
            onChange={event => setUsername(event.target.value)}
            required
          />
          <PasswordInput
            id="admin-password"
            labelText="Password"
            autoComplete="current-password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            required
          />
          <Button type="submit" size="lg">Sign in</Button>
        </form>

        <p className="admin-security-note">Temporary frontend authentication for development only. Replace it with server-side authentication before production use.</p>
        <Link className="back-dashboard-link" to="/"><ArrowLeft size={16} /> Return to dashboard</Link>
      </section>
    </main>
  );
};

const AdminConfiguration = ({ onLogout }) => {
  const [config, setConfig] = useState(loadDashboardConfig);
  const [notification, setNotification] = useState(null);

  const updateField = (field, value) => {
    setConfig(currentConfig => ({ ...currentConfig, [field]: value }));
    setNotification(null);
  };

  const handleSave = event => {
    event.preventDefault();
    const savedConfig = saveDashboardConfig(config);
    setConfig(savedConfig);
    setNotification({ kind: "success", title: "Configuration saved", subtitle: "The dashboard will use these settings the next time it is opened." });
  };

  const handleReset = () => {
    setConfig(resetDashboardConfig());
    setNotification({ kind: "info", title: "Defaults restored", subtitle: "The dashboard will use the default settings when it is opened." });
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-brand">
          <span><Security size={22} /></span>
          <div><strong>SGBV Administration</strong><small>Dashboard configuration</small></div>
        </div>
        <div className="admin-header-actions">
          <Link className="admin-dashboard-link" to="/"><ArrowLeft size={16} /> Dashboard</Link>
          <Button kind="ghost" renderIcon={Logout} onClick={onLogout}>Sign out</Button>
        </div>
      </header>

      <main className="admin-content">
        <div className="admin-intro">
          <span>System administration</span>
          <h1>Dashboard configuration</h1>
          <p>Manage dashboard branding and choose which analytical sections are available to dashboard users.</p>
        </div>

        {notification && (
          <InlineNotification
            kind={notification.kind}
            lowContrast
            title={notification.title}
            subtitle={notification.subtitle}
            onCloseButtonClick={() => setNotification(null)}
          />
        )}

        <form className="admin-config-form" onSubmit={handleSave}>
          <section className="admin-config-card" aria-labelledby="branding-settings-title">
            <div className="admin-card-heading">
              <span>01</span>
              <div><h2 id="branding-settings-title">Branding</h2><p>Customize the labels displayed in the dashboard header and footer.</p></div>
            </div>
            <div className="admin-fields-grid">
              <TextInput
                id="dashboard-title"
                labelText="Dashboard title"
                value={config.dashboardTitle}
                onChange={event => updateField("dashboardTitle", event.target.value)}
                required
              />
              <TextInput
                id="footer-organization"
                labelText="Footer organization"
                value={config.footerOrganization}
                onChange={event => updateField("footerOrganization", event.target.value)}
                required
              />
              <div className="admin-field-wide">
                <TextInput
                  id="dashboard-subtitle"
                  labelText="Dashboard subtitle"
                  value={config.dashboardSubtitle}
                  onChange={event => updateField("dashboardSubtitle", event.target.value)}
                  required
                />
              </div>
            </div>
          </section>

          <section className="admin-config-card" aria-labelledby="feature-settings-title">
            <div className="admin-card-heading">
              <span>02</span>
              <div><h2 id="feature-settings-title">Dashboard features</h2><p>Control which analytical sections appear on the public dashboard.</p></div>
            </div>
            <div className="admin-toggle-list">
              <div><div><strong>Demographic charts</strong><p>Show gender, age-group, and reporting-channel visualizations.</p></div><Toggle id="show-demographics" labelText="Demographic charts" labelA="Off" labelB="On" hideLabel toggled={config.showDemographics} onToggle={value => updateField("showDemographics", value)} /></div>
              <div><div><strong>AI-assisted insights</strong><p>Show aggregate summaries, quality checks, planning guidance, and dashboard questions.</p></div><Toggle id="show-ai-insights" labelText="AI-assisted insights" labelA="Off" labelB="On" hideLabel toggled={config.showAiInsights} onToggle={value => updateField("showAiInsights", value)} /></div>
              <div><div><strong>Service-delivery tables</strong><p>Show searchable health-facility and police-station service tables.</p></div><Toggle id="show-service-tables" labelText="Service-delivery tables" labelA="Off" labelB="On" hideLabel toggled={config.showServiceTables} onToggle={value => updateField("showServiceTables", value)} /></div>
            </div>
          </section>

          <div className="admin-form-actions">
            <Button type="button" kind="secondary" renderIcon={Reset} onClick={handleReset}>Restore defaults</Button>
            <Button type="submit" renderIcon={Save}>Save configuration</Button>
          </div>
        </form>

        <AdminDataManager />
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(hasAdminSession);

  const handleLogout = () => {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setAuthenticated(false);
  };

  return authenticated
    ? <AdminConfiguration onLogout={handleLogout} />
    : <AdminLogin onLogin={() => setAuthenticated(true)} />;
};

export default AdminPage;
